'use client'

import { useSelector } from 'react-redux'

/**
 * Hook to get translation function and current language.
 * Uses Redux global.language state.
 *
 * @param {Object} locales - Translation object with language keys (e.g., { zh_hant: {...}, en: {...} })
 * @returns {{ t: (key: string, fallback?: string) => string, language: string }}
 *
 * @example
 * // page.i18n.js
 * export default {
 *     zh_hant: { title: '標題' },
 *     en: { title: 'Title' }
 * }
 *
 * // page.jsx
 * import locales from './page.i18n'
 * const { t } = useTranslation(locales)
 * t('title') // → '標題' or 'Title'
 */
export function useTranslation(locales = {}) {
    const language = useSelector((state) => state.global.language) || 'zh_hant'

    /**
     * Get translated string for a key
     * @param {string} key - Translation key
     * @param {string} [fallback] - Fallback value if key not found
     * @returns {string} Translated string or fallback or key
     */
    const translations = locales[language] || {}

    const t = (key, fallback) => {
        return translations[key] ?? fallback ?? key
    }

    return { t, language, translations }
}
